import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { liveAgentSocket, getCurrentUserId } from "./liveAgentSocket";
import { notifyIncoming, buildContactMeta, shouldAlert } from "./toastBus";
import { hasPageAccess } from "../../../utils/pageAccess";

const PROSPECT_PATH = "/prospect-communication";

/**
 * App-wide "a customer sent a message" toast.
 *
 * LiveAgentProvider — the full console (transcripts, composer, takeover) —
 * only mounts on the Prospect Communication page, so an agent working anywhere
 * else in the app previously heard nothing until they switched back. This is a
 * much lighter listener: it keeps just enough state (session_id → session
 * snapshot, from the same frames) to caption a toast with the contact's name
 * and property/website + unit, shares the same ref-counted socket via
 * acquire/release, and reuses toastBus (sound, per-session throttle, cross-tab
 * de-dupe) so nothing double-fires.
 *
 * Silent while already on the Prospect Communication page — LiveAgentProvider's
 * own notify handler owns that surface (it knows which conversation panel is
 * open and skips the toast for it, which this component can't see).
 */
export default function GlobalLiveAgentNotifier() {
  const { isLogged, user } = useSelector((state) => state.user);
  const navigate = useNavigate();
  const location = useLocation();
  const enabled =
    isLogged &&
    (hasPageAccess(user, "prospect_communication") ||
      hasPageAccess(user, "chatbot_analytics"));

  const sessionsRef = useRef({});
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const pathRef = useRef(location.pathname);
  pathRef.current = location.pathname;

  useEffect(() => {
    if (!enabled) return undefined;

    const unsubscribe = liveAgentSocket.subscribe((event) => {
      if (event.kind !== "frame") return;
      const { frame } = event;

      if (frame.type === "live_list") {
        for (const s of frame.sessions || []) {
          sessionsRef.current[s.session_id] = s;
        }
        return;
      }

      if (frame.type === "presence" && frame.session?.session_id) {
        sessionsRef.current[frame.session.session_id] = frame.session;
        return;
      }

      if (frame.type === "notify") {
        // The full console already handles (and correctly suppresses) this
        // on its own page — don't pile a second toast on top of it.
        if (pathRef.current.startsWith(PROSPECT_PATH)) return;

        const session = sessionsRef.current[frame.session_id];
        if (!shouldAlert(session, getCurrentUserId())) return;

        notifyIncoming({
          sessionId: frame.session_id,
          turnId: frame.turn_id,
          name: session?.contact_name,
          meta: buildContactMeta(session),
          text: frame.text,
          onOpen: () =>
            navigateRef.current(`${PROSPECT_PATH}?session=${frame.session_id}`),
        });
      }
    });

    liveAgentSocket.acquire();
    return () => {
      unsubscribe();
      liveAgentSocket.release();
    };
  }, [enabled]);

  return null;
}
